import { useState, useEffect, useMemo } from "react";
import { Clock, Edit3, Trash2, MapPin, Camera, Video, Star, Heart, Users, Share2, Package, Calendar, Bell, X, ChevronRight, Plus, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import CreateReviewModal from "./CreateReviewModal";

interface DraftReview {
  id: string;
  restaurantId: string;
  restaurantName: string;
  restaurantImage?: string;
  type: 'dine-in' | 'takeout' | 'delivery';
  orderId?: string;
  eatzyClientId?: string;
  draftData: {
    rating?: number;
    text?: string;
    tags?: string[];
    media?: Array<{ type: 'photo' | 'video'; url: string; id: string }>;
    bestCategories?: string[];
    sharingSettings?: any;
    itemReviews?: any[];
  };
  lastEdited: string;
  visitDate?: string;
  orderDate?: string;
  progress: {
    hasRating: boolean;
    hasText: boolean;
    hasMedia: boolean;
    hasSharing: boolean;
    completionPercentage: number;
  };
}

interface PendingReview {
  id: string;
  type: 'order';
  restaurantId?: string;
  restaurantName: string;
  eatzyClientId?: string;
  orderDate?: string;
  orderId: string;
  orderTotal?: number;
  orderType?: 'takeout' | 'delivery';
  orderItems?: string[];
  orderItemsFull?: any[];
  daysAgo: number;
  urgency: 'low' | 'medium' | 'high';
}

interface EatzyOrderForReview {
  id: string;
  hash: string;
  restaurantName: string;
  restaurantId?: string;
  eatzyClientId?: string;
  items: string[];
  fullItems?: any[];
  total: number;
  status: string;
  date: Date;
  type?: 'takeout' | 'delivery';
}

interface DraftReviewsManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onReviewSelected?: (reviewData: any) => void;
  eatzyOrders?: EatzyOrderForReview[];
  reviewedOrderIds?: Set<string>;
}

const DRAFT_STORAGE_KEY = 'eatzy-review-draft';

function loadDraftFromStorage(): DraftReview | null {
  try {
    const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!savedDraft) return null;
    
    const draft = JSON.parse(savedDraft);
    if (!draft.restaurantName) return null;
    
    const hasRating = draft.overallRating > 0;
    const hasText = !!draft.reviewText && draft.reviewText.trim().length > 0;
    const hasMedia = (draft.photos?.length || 0) + (draft.videos?.length || 0) > 0;
    const hasSharing = true;
    
    let completionPercentage = 0;
    if (hasRating) completionPercentage += 40;
    if (hasText) completionPercentage += 30;
    if (hasMedia) completionPercentage += 20;
    if (hasSharing) completionPercentage += 10;
    
    return {
      id: 'draft_current',
      restaurantId: draft.restaurantId || '',
      restaurantName: draft.restaurantName,
      eatzyClientId: draft.eatzyClientId,
      type: draft.serviceType || 'dine-in',
      orderId: draft.orderId,
      draftData: {
        rating: draft.overallRating,
        text: draft.reviewText,
        tags: draft.selectedTags || [],
        media: [],
        itemReviews: draft.dishRatings
      },
      lastEdited: draft.savedAt || new Date().toISOString(),
      orderDate: draft.orderDate,
      progress: {
        hasRating,
        hasText,
        hasMedia,
        hasSharing,
        completionPercentage
      }
    };
  } catch {
    return null;
  }
}

export default function DraftReviewsManager({
  isOpen,
  onClose,
  onReviewSelected,
  eatzyOrders = [],
  reviewedOrderIds = new Set()
}: DraftReviewsManagerProps) {
  const [draftReview, setDraftReview] = useState<DraftReview | null>(null);
  const [selectedReview, setSelectedReview] = useState<DraftReview | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [pendingToReview, setPendingToReview] = useState<PendingReview | null>(null);
  const { toast } = useToast();
  
  useEffect(() => {
    if (isOpen) {
      const draft = loadDraftFromStorage();
      setDraftReview(draft);
    }
  }, [isOpen]);
  
  const draftReviews = draftReview ? [draftReview] : [];
  
  const completedStatuses = ['closed', 'delivered', 'completed'];
  
  const pendingReviews: PendingReview[] = useMemo(() => {
    return eatzyOrders
      .filter(order => {
        const isCompleted = completedStatuses.includes(order.status);
        const isNotReviewed = !reviewedOrderIds.has(order.hash);
        const isNotDraft = !draftReview || draftReview.orderId !== order.hash;
        return isCompleted && isNotReviewed && isNotDraft;
      })
      .map(order => {
        const daysAgo = Math.floor((Date.now() - order.date.getTime()) / (1000 * 60 * 60 * 24));
        return {
          id: order.id,
          type: 'order' as const,
          restaurantId: order.restaurantId,
          restaurantName: order.restaurantName,
          eatzyClientId: order.eatzyClientId,
          orderDate: order.date.toISOString(),
          orderId: order.hash,
          orderTotal: order.total,
          orderType: order.type || 'delivery',
          orderItems: order.items,
          orderItemsFull: order.fullItems,
          daysAgo,
          urgency: daysAgo > 3 ? 'high' as const : daysAgo > 1 ? 'medium' as const : 'low' as const
        };
      })
      .sort((a, b) => b.daysAgo - a.daysAgo);
  }, [eatzyOrders, reviewedOrderIds, draftReview]);

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays === 0) {
      if (diffHours === 0) return "Just now";
      return `${diffHours}h ago`;
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 80) return "text-green-600";
    if (percentage >= 50) return "text-yellow-600";
    return "text-red-600";
  };

  const handleEditDraft = (draft: DraftReview) => {
    setSelectedReview(draft);
    onReviewSelected?.(draft);
    onClose();
  };

  const handleDeleteDraft = (draftId: string) => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    window.dispatchEvent(new Event('draftChanged'));
    setDraftReview(null);
    toast({
      title: "Draft Deleted",
      description: "Your draft review has been removed.",
    });
  };

  const handleStartPendingReview = (pending: PendingReview) => {
    setPendingToReview(pending);
    setShowCreateModal(true);
  };

  const getOrderDataForPending = (pending: PendingReview | null) => {
    if (!pending) return undefined;
    
    return {
      id: pending.orderId,
      restaurantId: pending.restaurantId,
      eatzyClientId: pending.eatzyClientId,
      restaurantName: pending.restaurantName,
      type: pending.orderType || 'delivery',
      orderDate: pending.orderDate || new Date().toISOString(),
      items: pending.orderItemsFull || (pending.orderItems || []).map((itemName, index) => ({
        itemId: `item_${index}`,
        itemName,
        quantity: 1,
        price: 0
      }))
    };
  };

  const handleReviewCreated = (reviewData: any) => {
    setPendingToReview(null);
    setShowCreateModal(false);
    toast({
      title: "Review Submitted!",
      description: "Thank you for sharing your experience.",
    });
  };

  const getServiceTypeLabel = (type: string) => {
    switch (type) {
      case 'dine-in': return 'Dine Out';
      case 'takeout': return 'Takeout';
      case 'delivery': return 'Delivery';
      default: return type;
    }
  };

  const DraftCard = ({ draft }: { draft: DraftReview }) => (
    <Card className="hover-elevate" data-testid={`draft-card-${draft.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              {draft.restaurantName}
              <Badge variant="secondary">
                {getServiceTypeLabel(draft.type)}
              </Badge>
            </CardTitle>
            <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
              <span>Last edited {formatTimeAgo(draft.lastEdited)}</span>
              <span className={getProgressColor(draft.progress.completionPercentage)}>
                {draft.progress.completionPercentage}% complete
              </span>
            </div>
          </div>
          <div className="flex gap-1">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => handleEditDraft(draft)}
              data-testid={`button-edit-${draft.id}`}
            >
              <Edit3 className="w-4 h-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => handleDeleteDraft(draft.id)}
              data-testid={`button-delete-${draft.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Progress indicators */}
        <div className="flex gap-2">
          {draft.progress.hasRating && (
            <Badge variant="outline" className="text-xs">
              <Star className="w-3 h-3 mr-1" />
              Rating
            </Badge>
          )}
          {draft.progress.hasText && (
            <Badge variant="outline" className="text-xs">
              <Edit3 className="w-3 h-3 mr-1" />
              Text
            </Badge>
          )}
          {draft.progress.hasMedia && (
            <Badge variant="outline" className="text-xs">
              <Camera className="w-3 h-3 mr-1" />
              Media
            </Badge>
          )}
          {draft.progress.hasSharing && (
            <Badge variant="outline" className="text-xs">
              <Share2 className="w-3 h-3 mr-1" />
              Sharing
            </Badge>
          )}
        </div>

        {/* Preview of content */}
        {draft.draftData.text && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            "{draft.draftData.text}"
          </p>
        )}

        {/* Media preview */}
        {draft.draftData.media && draft.draftData.media.length > 0 && (
          <div className="flex gap-2">
            {draft.draftData.media.slice(0, 3).map((media, index) => (
              <div key={index} className="w-12 h-12 bg-muted rounded-md flex items-center justify-center">
                {media.type === 'photo' ? (
                  <Camera className="w-4 h-4" />
                ) : (
                  <Video className="w-4 h-4" />
                )}
              </div>
            ))}
            {draft.draftData.media.length > 3 && (
              <div className="w-12 h-12 bg-muted rounded-md flex items-center justify-center text-xs">
                +{draft.draftData.media.length - 3}
              </div>
            )}
          </div>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => handleEditDraft(draft)}
          className="w-full"
          data-testid={`button-continue-${draft.id}`}
        >
          Continue Review
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );

  const PendingCard = ({ pending }: { pending: PendingReview }) => (
    <Card className="hover-elevate" data-testid={`pending-card-${pending.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Package className="w-4 h-4" />
              {pending.restaurantName}
              <Badge variant={getUrgencyColor(pending.urgency)} className="text-xs">
                {pending.daysAgo > 3 ? 'High' : pending.daysAgo > 1 ? 'Medium' : 'Low'} priority
              </Badge>
            </CardTitle>
            <div className="text-sm text-muted-foreground mt-1">
              Order completed {pending.daysAgo} day{pending.daysAgo !== 1 ? 's' : ''} ago
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formatTimeAgo(pending.orderDate || new Date().toISOString())}
          </div>
          {pending.orderTotal && (
            <div className="flex items-center gap-1">
              <span>${pending.orderTotal.toFixed(2)}</span>
            </div>
          )}
          {pending.orderType && (
            <Badge variant="outline" className="text-xs">
              {pending.orderType === 'delivery' ? 'Delivery' : 'Takeout'}
            </Badge>
          )}
        </div>

        {pending.orderItems && pending.orderItems.length > 0 && (
          <div className="text-sm text-muted-foreground">
            {pending.orderItems.slice(0, 3).join(', ')}
            {pending.orderItems.length > 3 && ` +${pending.orderItems.length - 3} more`}
          </div>
        )}

        <Button
          size="sm"
          onClick={() => handleStartPendingReview(pending)}
          className="w-full"
          data-testid={`button-start-review-${pending.id}`}
        >
          Write Review
          <Plus className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90svh] overflow-y-auto" data-testid="draft-reviews-manager">
          <DialogHeader>
            <DialogTitle>Review Center</DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="pending" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="pending" className="flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Pending Reviews ({pendingReviews.length})
              </TabsTrigger>
              <TabsTrigger value="drafts" className="flex items-center gap-2">
                <Edit3 className="w-4 h-4" />
                Draft Reviews ({draftReviews.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pending" className="space-y-4">
              {pendingReviews.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-8 text-center">
                    <Bell className="w-12 h-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium">All caught up!</h3>
                    <p className="text-muted-foreground">
                      No pending reviews at the moment.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <Alert>
                    <Bell className="w-4 h-4" />
                    <AlertDescription>
                      We found {pendingReviews.length} restaurant{pendingReviews.length > 1 ? 's' : ''} you visited recently. 
                      Share your experience to help others and earn Kudoz!
                    </AlertDescription>
                  </Alert>

                  <div className="grid gap-4">
                    {pendingReviews.map(pending => (
                      <PendingCard key={pending.id} pending={pending} />
                    ))}
                  </div>
                </>
              )}
            </TabsContent>

            <TabsContent value="drafts" className="space-y-4">
              {draftReviews.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-8 text-center">
                    <Edit3 className="w-12 h-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium">No draft reviews</h3>
                    <p className="text-muted-foreground">
                      You don't have any saved draft reviews.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <Alert>
                    <Edit3 className="w-4 h-4" />
                    <AlertDescription>
                      You have {draftReviews.length} unfinished review{draftReviews.length > 1 ? 's' : ''}. 
                      Complete them to share your experiences and earn Kudoz!
                    </AlertDescription>
                  </Alert>

                  <div className="grid gap-4">
                    {draftReviews.map(draft => (
                      <DraftCard key={draft.id} draft={draft} />
                    ))}
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Create Review Modal for Pending Reviews */}
      {showCreateModal && pendingToReview && (
        <CreateReviewModal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setPendingToReview(null);
          }}
          onReviewSubmitted={handleReviewCreated}
          restaurantName={pendingToReview.restaurantName}
          restaurantId={pendingToReview.restaurantId}
          orderId={pendingToReview.orderId}
          orderData={getOrderDataForPending(pendingToReview)}
        />
      )}
    </>
  );
}